import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";

import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { useApiCall } from "@framework/react-hooks";
import type { IUser } from "@framework/types";

import { useSpeed } from "../../../../../../context/speed-context";
import { formatTrendTooltipDate, isFiniteNumber } from "../../../../../../utils";
import {
  MultilineChartOrbit,
  RotationDirection,
} from "../../../scheme/components/analytics/components/multiline-chart-orbit";
import {
  MultilineChartSignal,
  type ISyncedCursorAction,
} from "../../../scheme/components/analytics/components/multiline-chart-signal";
import { getSignalMarkerPassageXValues } from "../../../scheme/components/analytics/components/multiline-chart-signal/marker-points";
import {
  ANALYTICS_MEASURE_TYPE,
  TRAIN_ROTATION_DIRECTION,
  type IChartDataOrbit,
  type IChartDataSignal,
  type ISignalDataResponse,
} from "../../../scheme/components/analytics/interfaces";
import { cartesianToPolar } from "../../../scheme/components/analytics/mappers/orbitMappers";
import { transformSingleSignalToChart } from "../../../scheme/components/analytics/mappers/signalMappers";
import { lineColors } from "../../../scheme/components/analytics/constants";
import { useTrainRotationDirection } from "../../../multi-analysis/hooks/use-train-rotation-direction";
import {
  ROTATION_DIRECTION,
  SIGNAL_TIME_SCALE,
  type IOrbitBandSelection,
  type IOrbitRevolutionSelection,
  type TSignalTimeScale,
} from "../../../multi-analysis/types";
import { estimateOrbitSamplingRateHz } from "../../../scheme/components/analytics/utils/orbit-sampling-rate";
import {
  filterOrbitSamples,
  ORBIT_ORIGINAL_FORM_COLOR,
} from "../../../scheme/components/analytics/utils/orbit-sample-filter";
import { parseMultiAnalysisChartTime } from "../../../multi-analysis/utils/range-frame";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import type { ISpeedScreenFormContentProps } from "../types";
import { formatOrbitWindowHeader, isOrbitPairMark, type IOrbitChannel, type IOrbitPairMark } from "./pair-marks";
import { orbitBandSyncValues, placeOrbitBandPoint, sliceOrbitToBand } from "./band-selection";
import {
  orbitRevolutionSyncValues,
  placeOrbitRevolutionCursor,
  sameRevolutionBand,
  shiftOrbitRevolutionCursor,
} from "./revolution-selection";

interface IOrbitWaveformResponse extends ISignalDataResponse {
  param3?: number | null;
  param3DataMs?: number | null;
}

const mapToolbarRotationDirection = (direction: ROTATION_DIRECTION): RotationDirection => {
  switch (direction) {
    case ROTATION_DIRECTION.COUNTER_CLOCKWISE:
      return RotationDirection.COUNTER_CLOCKWISE;
    case ROTATION_DIRECTION.CLOCKWISE:
      return RotationDirection.CLOCKWISE;
    case ROTATION_DIRECTION.UNDEFINED:
    default:
      return RotationDirection.UNDEFINED;
  }
};

const withSignalSamples = (
  signal: IChartDataSignal,
  samples: readonly number[] | null,
  color: string,
): IChartDataSignal => {
  if (!samples) {
    return { ...signal, color };
  }

  return {
    ...signal,
    color,
    items: signal.items.map((item, index) => ({
      ...item,
      y: index < samples.length ? samples[index] : item.y,
    })),
  };
};

const buildOrbitSignalSeries = (
  signal: IChartDataSignal,
  samples: readonly number[] | null,
  showOriginalForm: boolean,
): IChartDataSignal[] => {
  if (!samples) {
    return [signal];
  }

  const filtered = withSignalSamples(signal, samples, signal.color);
  if (!showOriginalForm) {
    return [filtered];
  }

  return [withSignalSamples(signal, null, ORBIT_ORIGINAL_FORM_COLOR), filtered];
};

const getOrbitSignalAxisDisplay = (signal: IChartDataSignal | null, timeScale: TSignalTimeScale) => {
  const rotationFrequencyRpm = signal?.rotationFrequencyRpm;
  if (signal == null || !isFiniteNumber(rotationFrequencyRpm) || rotationFrequencyRpm <= 0) {
    return undefined;
  }

  const targetUnitsCode = timeScale === SIGNAL_TIME_SCALE.REVOLUTIONS ? 6 : 5;
  const factor =
    signal.unitsXCode === targetUnitsCode
      ? 1
      : targetUnitsCode === 6
        ? rotationFrequencyRpm / 60_000
        : 60_000 / rotationFrequencyRpm;

  return { factor, unitsCode: targetUnitsCode };
};

const matchByMeasure = <T extends { idMeasure: number; date: string }>(
  rows: readonly T[],
  channel: IOrbitChannel,
): T | null => {
  const sameMeasure = rows.filter(row => row.idMeasure === channel.idMeasure);
  if (!sameMeasure.length) return null;
  if (sameMeasure.length === 1) return sameMeasure[0];

  const target = parseMultiAnalysisChartTime(channel.date);
  return sameMeasure.reduce((best, row) =>
    Math.abs(parseMultiAnalysisChartTime(row.date) - target) < Math.abs(parseMultiAnalysisChartTime(best.date) - target)
      ? row
      : best,
  );
};

const orbitZeroIndexes = (resolution: number, revolutions: number): number[] => {
  const indexes: number[] = [];
  let revolution = 0;

  do {
    indexes.push(Math.floor((revolution * resolution) / revolutions + 0.5));
    revolution += 1;
  } while (revolution < revolutions);

  return indexes;
};

const buildOrbitSeries = (
  xSignal: IOrbitWaveformResponse,
  ySignal: IOrbitWaveformResponse,
  xPickupAngle: number | null,
  yPickupAngle: number | null,
  units: string,
): IChartDataOrbit => {
  const length = Math.min(xSignal.data.length, ySignal.data.length);
  const xData = xSignal.data.slice(0, length);
  const yData = ySignal.data.slice(0, length);
  const resolution = ySignal.resolution ?? undefined;
  const revolutions = isFiniteNumber(ySignal.param3) ? ySignal.param3 : null;
  const rangeType = ySignal.rangeType;
  const rotationFrequencyHz = isFiniteNumber(ySignal.rotationFrequencyRpm) ? ySignal.rotationFrequencyRpm / 60 : null;
  const zeroIndexes =
    rangeType === 2 && resolution != null && revolutions != null && revolutions > 0
      ? orbitZeroIndexes(resolution, revolutions)
      : [];
  const items = cartesianToPolar(xData, yData, zeroIndexes);
  const maxAbs = (values: readonly number[]) => values.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
  return {
    name: ySignal.name,
    parameterName: ySignal.name,
    idPoint: ySignal.idPoint,
    color: lineColors[0],
    items,
    units,
    unitCode: ySignal.units,
    xData,
    yData,
    zeroIndexes,
    Xmax: maxAbs(xData),
    Ymax: maxAbs(yData),
    Smax: items.reduce((max, item) => Math.max(max, item.r), 0),
    resolution,
    param3: revolutions ?? undefined,
    param3DataMs: ySignal.param3DataMs ?? null,
    rangeType,
    rotationFrequencyHz,
    xPickupAngle,
    yPickupAngle,
  };
};

interface IOrbitPanelData {
  key: string;
  mark: IOrbitPairMark;
  orbit: IChartDataOrbit | null;
  xSignal: IChartDataSignal | null;
  ySignal: IChartDataSignal | null;
}

const panelSignals = (panels: readonly IOrbitPanelData[]): IChartDataSignal[] =>
  panels.flatMap(panel =>
    [panel.xSignal, panel.ySignal].filter((signal): signal is IChartDataSignal => signal != null),
  );

const canShowMarkerPoints = (signals: readonly IChartDataSignal[]): boolean =>
  signals.some(signal => getSignalMarkerPassageXValues(signal).length > 0);

interface IOrbitBandControls {
  enabled: boolean;
  selection: IOrbitBandSelection | null;
  onPoint: (panelKey: string, nativeX: number, datasetIndex: number) => void;
  onClear: () => void;
}

interface IOrbitRevolutionControls {
  enabled: boolean;
  selection: IOrbitRevolutionSelection | null;
  onPoint: (panelKey: string, nativeX: number, datasetIndex: number) => void;
  onStep: (direction: -1 | 1) => boolean;
}

const displayOrbitForPanel = (
  panel: IOrbitPanelData,
  band: IOrbitBandControls,
  revolution: IOrbitRevolutionControls,
): IChartDataOrbit | null => {
  if (!panel.orbit) {
    return null;
  }

  if (revolution.enabled && revolution.selection?.panelKey === panel.key) {
    return sliceOrbitToBand(
      panel.orbit,
      panel.xSignal ?? panel.ySignal,
      revolution.selection.bandStartNativeX,
      revolution.selection.bandEndNativeX,
    );
  }

  if (
    !band.enabled ||
    !band.selection ||
    band.selection.panelKey !== panel.key ||
    band.selection.markerNativeX == null ||
    band.selection.cursorNativeX == null
  ) {
    return panel.orbit;
  }

  return sliceOrbitToBand(
    panel.orbit,
    panel.xSignal ?? panel.ySignal,
    band.selection.markerNativeX,
    band.selection.cursorNativeX,
  );
};

const OrbitBandSignal: FC<{
  data: IChartDataSignal[];
  datasetKeys: readonly string[];
  band: IOrbitBandControls;
  revolution: IOrbitRevolutionControls;
  showMarkerPoints: boolean;
  axis: { factor: number; unitsCode: number } | undefined;
  onHeaderTooltipChange: (text: string | null) => void;
  foregroundDatasetIndexes?: readonly number[] | null;
  onActivate?: (nativeX: number, datasetIndex: number) => void;
}> = ({
  data,
  datasetKeys,
  band,
  revolution,
  showMarkerPoints,
  axis,
  onHeaderTooltipChange,
  foregroundDatasetIndexes = null,
  onActivate,
}) => {
  const { formatMessage } = useIntl();
  const datasetKeySignature = datasetKeys.join("\u0000");
  const sync = useMemo(() => {
    const keys = datasetKeySignature ? datasetKeySignature.split("\u0000") : [];
    if (revolution.enabled) {
      return orbitRevolutionSyncValues(keys, revolution.selection);
    }

    return orbitBandSyncValues(keys, band.enabled ? band.selection : null);
  }, [band.enabled, band.selection, datasetKeySignature, revolution.enabled, revolution.selection]);
  const onPointRef = useRef(band.onPoint);
  const onRevolutionPointRef = useRef(revolution.onPoint);
  const onRevolutionStepRef = useRef(revolution.onStep);
  const revolutionEnabledRef = useRef(revolution.enabled);
  const keysRef = useRef(datasetKeys);
  onPointRef.current = band.onPoint;
  onRevolutionPointRef.current = revolution.onPoint;
  onRevolutionStepRef.current = revolution.onStep;
  revolutionEnabledRef.current = revolution.enabled;
  keysRef.current = datasetKeys;
  const handleAction = useCallback((action: ISyncedCursorAction) => {
    const keys = keysRef.current;
    const panelKey = keys[action.datasetIndex] ?? keys[0];
    if (!panelKey || action.nativeX == null || !Number.isFinite(action.nativeX)) {
      return;
    }

    if (revolutionEnabledRef.current) {
      onRevolutionPointRef.current(panelKey, action.nativeX, action.datasetIndex);
      return;
    }

    onPointRef.current(panelKey, action.nativeX, action.datasetIndex);
  }, []);
  const handleKeyboardStep = useCallback((direction: -1 | 1) => onRevolutionStepRef.current(direction), []);
  const cursorPlacementEnabled = band.enabled || revolution.enabled;
  const resetZoomRef = useRef<(() => void) | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const handleRegisterReset = useCallback((handler: (() => void) | null) => {
    resetZoomRef.current = handler;
  }, []);
  const resetZoomLabel = formatMessage({ id: "pages.scheme.analytics.resetZoom" });

  if (!data.length) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 0 }}>
      {isZoomed ? (
        <Tooltip title={resetZoomLabel} arrow disableInteractive>
          <IconButton
            size="small"
            aria-label={resetZoomLabel}
            onClick={event => {
              event.stopPropagation();
              resetZoomRef.current?.();
            }}
            sx={{ position: "absolute", top: 4, left: 4, zIndex: 2, bgcolor: "background.paper" }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <MultilineChartSignal
        data={data}
        chartTitle=""
        tooltipInHeader
        onHeaderTooltipChange={onHeaderTooltipChange}
        showMarkerPoints={showMarkerPoints}
        displayCascade3D={false}
        foregroundDatasetIndexes={foregroundDatasetIndexes}
        enableKeyboardCursorNavigation={revolution.enabled}
        onKeyboardStep={revolution.enabled ? handleKeyboardStep : undefined}
        markerMode={band.enabled}
        markerTooltipType={band.enabled ? "signal" : undefined}
        syncCursorValues={sync.cursorValues}
        syncMarkerValues={sync.markerValues}
        syncCursorDatasetIndex={sync.datasetIndex}
        syncMarkerDatasetIndex={sync.datasetIndex}
        onSyncedCursorAction={cursorPlacementEnabled ? handleAction : undefined}
        onNativeXClick={onActivate}
        onMiddleClick={band.enabled ? band.onClear : undefined}
        registerReset={handleRegisterReset}
        onZoomStateChange={setIsZoomed}
        xAxisDisplayScale={axis?.factor}
        xAxisDisplayUnits={axis ? formatMessage({ id: `enums.units.${axis.unitsCode}` }) : undefined}
        xAxisDisplayUnitsCode={axis?.unitsCode}
      />
    </Box>
  );
};

const OrbitPanel: FC<{
  panel: IOrbitPanelData;
  fill?: boolean;
  externalControls: {
    hideInlineControls: boolean;
    rotationDirection?: RotationDirection;
    showOrbitPoints: boolean;
    orbitFilterOpen: boolean;
    appliedOrbitBins: readonly number[];
    appliedBandpassHz: { lo: number; hi: number } | null;
    showOriginalForm: boolean;
    onOrbitFilterOpenChange: (open: boolean) => void;
    onAppliedOrbitBinsChange: (bins: readonly number[]) => void;
    onAppliedBandpassHzChange: (value: { lo: number; hi: number } | null) => void;
    onShowOriginalFormChange?: (enabled: boolean) => void;
  };
  hostFilterDialog: boolean;
  signalTimeScale: TSignalTimeScale;
  trainRotationDirection: TRAIN_ROTATION_DIRECTION;
  dataFormat: IUser["interfaceSettings"]["dataFormat"];
  band: IOrbitBandControls;
  revolution: IOrbitRevolutionControls;
}> = ({
  panel,
  fill = false,
  externalControls,
  hostFilterDialog,
  signalTimeScale,
  trainRotationDirection,
  dataFormat,
  band,
  revolution,
}) => {
  const [cursorLabel, setCursorLabel] = useState<string | null>(null);
  const filteredSamples = useMemo(
    () =>
      panel.orbit
        ? filterOrbitSamples(panel.orbit.xData, panel.orbit.yData, {
            bins: externalControls.appliedOrbitBins,
            bandpassHz: externalControls.appliedBandpassHz,
            samplingRateHz: estimateOrbitSamplingRateHz(panel.orbit),
            resolution: panel.orbit.resolution,
            param3: Number(panel.orbit.param3),
          })
        : null,
    [externalControls.appliedBandpassHz, externalControls.appliedOrbitBins, panel.orbit],
  );
  const showOriginalForm = externalControls.showOriginalForm && filteredSamples != null;
  const xSeries = useMemo(
    () => (panel.xSignal ? buildOrbitSignalSeries(panel.xSignal, filteredSamples?.x ?? null, showOriginalForm) : []),
    [filteredSamples, panel.xSignal, showOriginalForm],
  );
  const ySeries = useMemo(
    () => (panel.ySignal ? buildOrbitSignalSeries(panel.ySignal, filteredSamples?.y ?? null, showOriginalForm) : []),
    [filteredSamples, panel.ySignal, showOriginalForm],
  );
  const displayedOrbit = useMemo(
    () => displayOrbitForPanel(panel, band, revolution),
    [
      band,
      panel,
      revolution.enabled,
      revolution.selection?.panelKey,
      revolution.selection?.bandStartNativeX,
      revolution.selection?.bandEndNativeX,
    ],
  );
  const orbitChartData = useMemo(() => (displayedOrbit ? [displayedOrbit] : []), [displayedOrbit]);
  const chartControls = {
    ...externalControls,
    orbitFilterOpen: hostFilterDialog ? externalControls.orbitFilterOpen : false,
    onShowOriginalFormChange: hostFilterDialog ? externalControls.onShowOriginalFormChange : undefined,
  };
  const control = panel.mark.orbitPair.controlParameter;
  const controlLabel = control
    ? `${control.title} ${formatMeasurementNumber(control.value, null, dataFormat, { fallbackDigits: 2 })} ${
        control.units
      }`.trim()
    : null;
  const header = formatOrbitWindowHeader({
    pointTitle: panel.mark.orbitPair.pointTitle,
    measureName: panel.mark.orbitPair.measureName,
    dateLabel: formatTrendTooltipDate(panel.mark.date),
    controlLabel,
    cursorLabel,
  });

  return (
    <Box
      sx={{
        flex: fill ? 1 : "0 0 auto",
        minHeight: fill ? 0 : 360,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
      }}
    >
      <Typography
        title={header}
        sx={{
          px: 1,
          py: 0.75,
          fontSize: 12,
          fontWeight: 600,
          color: "#2e3445",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {header}
      </Typography>
      <Box
        sx={{
          ...(fill ? { flex: 1 } : { height: 320 }),
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          {orbitChartData.length ? (
            <MultilineChartOrbit
              data={orbitChartData}
              chartTitle=""
              isPolarTrendChecked
              trainRotationDirection={trainRotationDirection}
              externalControls={chartControls}
              tooltipInHeader
              onHeaderTooltipChange={setCursorLabel}
              onMiddleClick={band.enabled ? band.onClear : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0, minHeight: 0, display: "grid", gridTemplateRows: "1fr 1fr", gap: 1 }}>
          {[
            { series: xSeries, signal: panel.xSignal },
            { series: ySeries, signal: panel.ySignal },
          ].map(({ series, signal }, index) => (
            <Box key={index} sx={{ minWidth: 0, minHeight: 0 }}>
              <OrbitBandSignal
                data={series}
                datasetKeys={series.map(() => panel.key)}
                band={band}
                revolution={revolution}
                showMarkerPoints={externalControls.showOrbitPoints}
                axis={getOrbitSignalAxisDisplay(signal, signalTimeScale)}
                onHeaderTooltipChange={setCursorLabel}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const OrbitOverlay: FC<{
  panels: IOrbitPanelData[];
  activeKey: string | null;
  onActivate: (seriesKey: string) => void;
  externalControls: {
    hideInlineControls: boolean;
    rotationDirection?: RotationDirection;
    showOrbitPoints: boolean;
    orbitFilterOpen: boolean;
    appliedOrbitBins: readonly number[];
    appliedBandpassHz: { lo: number; hi: number } | null;
    showOriginalForm: boolean;
    onOrbitFilterOpenChange: (open: boolean) => void;
    onAppliedOrbitBinsChange: (bins: readonly number[]) => void;
    onAppliedBandpassHzChange: (value: { lo: number; hi: number } | null) => void;
    onShowOriginalFormChange?: (enabled: boolean) => void;
  };
  signalTimeScale: TSignalTimeScale;
  trainRotationDirection: TRAIN_ROTATION_DIRECTION;
  dataFormat: IUser["interfaceSettings"]["dataFormat"];
  band: IOrbitBandControls;
  revolution: IOrbitRevolutionControls;
}> = ({
  panels,
  activeKey,
  onActivate,
  externalControls,
  signalTimeScale,
  trainRotationDirection,
  dataFormat,
  band,
  revolution,
}) => {
  const [cursorLabel, setCursorLabel] = useState<string | null>(null);
  const activePanel = panels.find(panel => panel.key === activeKey) ?? panels[panels.length - 1] ?? null;
  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  const channels = useMemo(() => {
    const xData: IChartDataSignal[] = [];
    const yData: IChartDataSignal[] = [];
    const xKeys: string[] = [];
    const yKeys: string[] = [];

    panels.forEach(panel => {
      const filtered = panel.orbit
        ? filterOrbitSamples(panel.orbit.xData, panel.orbit.yData, {
            bins: externalControls.appliedOrbitBins,
            bandpassHz: externalControls.appliedBandpassHz,
            samplingRateHz: estimateOrbitSamplingRateHz(panel.orbit),
            resolution: panel.orbit.resolution,
            param3: Number(panel.orbit.param3),
          })
        : null;
      const showOriginal = externalControls.showOriginalForm && filtered != null;
      const append = (
        signal: IChartDataSignal | null,
        samples: readonly number[] | null,
        data: IChartDataSignal[],
        keys: string[],
      ) => {
        if (!signal) {
          return;
        }

        buildOrbitSignalSeries(signal, samples, showOriginal).forEach(series => {
          data.push(series);
          keys.push(panel.key);
        });
      };

      append(panel.xSignal, filtered?.x ?? null, xData, xKeys);
      append(panel.ySignal, filtered?.y ?? null, yData, yKeys);
    });

    return { xData, yData, xKeys, yKeys };
  }, [
    externalControls.appliedBandpassHz,
    externalControls.appliedOrbitBins,
    externalControls.showOriginalForm,
    panels,
  ]);

  const xKeysRef = useRef(channels.xKeys);
  const yKeysRef = useRef(channels.yKeys);
  xKeysRef.current = channels.xKeys;
  yKeysRef.current = channels.yKeys;

  const handleXClick = useCallback((_: number, datasetIndex: number) => {
    const key = xKeysRef.current[datasetIndex];
    if (key) {
      onActivateRef.current(key);
    }
  }, []);
  const handleYClick = useCallback((_: number, datasetIndex: number) => {
    const key = yKeysRef.current[datasetIndex];
    if (key) {
      onActivateRef.current(key);
    }
  }, []);

  const xForeground = useMemo(
    () => channels.xKeys.flatMap((key, index) => (key === activePanel?.key ? [index] : [])),
    [activePanel?.key, channels.xKeys],
  );
  const yForeground = useMemo(
    () => channels.yKeys.flatMap((key, index) => (key === activePanel?.key ? [index] : [])),
    [activePanel?.key, channels.yKeys],
  );
  const orbits = useMemo(
    () =>
      panels.flatMap(panel => {
        const orbit = displayOrbitForPanel(panel, band, revolution);
        return orbit ? [orbit] : [];
      }),
    [
      band,
      panels,
      revolution.enabled,
      revolution.selection?.panelKey,
      revolution.selection?.bandStartNativeX,
      revolution.selection?.bandEndNativeX,
    ],
  );

  const describePanel = (panel: IOrbitPanelData, cursor: string | null) => {
    const control = panel.mark.orbitPair.controlParameter;
    const controlLabel = control
      ? `${control.title} ${formatMeasurementNumber(control.value, null, dataFormat, { fallbackDigits: 2 })} ${
          control.units
        }`.trim()
      : null;

    return formatOrbitWindowHeader({
      pointTitle: panel.mark.orbitPair.pointTitle,
      measureName: panel.mark.orbitPair.measureName,
      dateLabel: formatTrendTooltipDate(panel.mark.date),
      controlLabel,
      cursorLabel: cursor,
    });
  };

  const header = activePanel ? describePanel(activePanel, cursorLabel) : "";
  const xAxis = getOrbitSignalAxisDisplay(activePanel?.xSignal ?? null, signalTimeScale);
  const yAxis = getOrbitSignalAxisDisplay(activePanel?.ySignal ?? null, signalTimeScale);
  const signalCharts = [
    { id: "x", data: channels.xData, foreground: xForeground, onClick: handleXClick, axis: xAxis },
    { id: "y", data: channels.yData, foreground: yForeground, onClick: handleYClick, axis: yAxis },
  ];

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 1, pt: 1 }}>
        {panels.map(panel => {
          const label = describePanel(panel, null);
          const active = panel.key === activePanel?.key;
          return (
            <Box
              key={panel.key}
              title={label}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, maxWidth: 320 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: panel.orbit?.color ?? lineColors[0],
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  color: active ? "#2e3445" : "#5c657a",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Typography
        title={header}
        sx={{
          px: 1,
          py: 0.75,
          fontSize: 12,
          fontWeight: 600,
          color: "#2e3445",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {header}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
          gap: 1,
          px: 1,
          pb: 1,
        }}
      >
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          {orbits.length ? (
            <MultilineChartOrbit
              data={orbits}
              chartTitle=""
              isPolarTrendChecked
              trainRotationDirection={trainRotationDirection}
              externalControls={externalControls}
              tooltipInHeader
              onHeaderTooltipChange={setCursorLabel}
              frontSeriesKey={activePanel?.key ?? null}
              onSeriesClick={onActivate}
              onMiddleClick={band.enabled ? band.onClear : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0, minHeight: 0, display: "grid", gridTemplateRows: "1fr 1fr", gap: 1 }}>
          {signalCharts.map(chart => (
            <Box key={chart.id} sx={{ minWidth: 0, minHeight: 0 }}>
              <OrbitBandSignal
                data={chart.data}
                datasetKeys={chart.id === "x" ? channels.xKeys : channels.yKeys}
                band={band}
                revolution={revolution}
                showMarkerPoints={externalControls.showOrbitPoints}
                axis={chart.axis}
                onHeaderTooltipChange={setCursorLabel}
                foregroundDatasetIndexes={chart.foreground}
                onActivate={chart.onClick}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const OrbitScreenForm: FC<ISpeedScreenFormContentProps> = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useUser<IUser>();
  const { selectedItem } = useAsu();
  const { dataMarksError, effectiveDataMarks, toolbarState, setToolbarState } = useSpeed();
  const trainId = selectedItem && "TrainId" in selectedItem ? Number(selectedItem.TrainId) : null;
  const marks = useMemo(() => effectiveDataMarks.filter(isOrbitPairMark), [effectiveDataMarks]);
  const { trainRotationDirection } = useTrainRotationDirection({ enabled: marks.length > 0, trainId });
  const requestKey = marks
    .map(
      mark =>
        `${mark.orbitPair.x.idMeasure}:${mark.orbitPair.x.date}|${mark.orbitPair.y.idMeasure}:${mark.orbitPair.y.date}`,
    )
    .join(";");
  const [loaded, setLoaded] = useState<{ key: string; panels: IOrbitPanelData[] } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const { fn: loadCharts } = useApiCall(
    async (api, requestMarks: IOrbitPairMark[]) => {
      const seen = new Set<string>();
      const signalRefs = requestMarks.flatMap(mark =>
        [mark.orbitPair.x, mark.orbitPair.y].flatMap(channel => {
          const key = `${channel.idMeasure}@${channel.date}`;
          if (seen.has(key)) return [];
          seen.add(key);
          return [{ idMeasure: channel.idMeasure, date: channel.date }];
        }),
      );
      const response = (await api.fetchJson({
        url: "/point-v2/measure/signal/data",
        method: "POST",
        data: { measures: signalRefs },
      })) as IOrbitWaveformResponse[] | null | undefined;
      const signals = Array.isArray(response) ? response : [];
      if (signals.length !== signalRefs.length) {
        throw new Error("orbitSignalBatchIncomplete");
      }

      const unitLabel = (code: number | null | undefined, fallback: string) =>
        code != null ? formatMessage({ id: `enums.units.${code}` }) : fallback;

      return requestMarks.map((mark): IOrbitPanelData => {
        const xSignal = matchByMeasure(signals, mark.orbitPair.x);
        const ySignal = matchByMeasure(signals, mark.orbitPair.y);
        if (!xSignal || !ySignal) {
          throw new Error("orbitSignalBatchIncomplete");
        }
        const signalChart = (signal: IOrbitWaveformResponse | null) => {
          if (!signal) return null;
          const chart = transformSingleSignalToChart(
            signal,
            mark.orbitPair.pointTitle,
            ANALYTICS_MEASURE_TYPE.SIGNAL_FORM,
          );
          return {
            ...chart,
            unitsX: unitLabel(chart.unitsXCode, chart.unitsX),
            unitsY: unitLabel(chart.unitsYCode, chart.unitsY),
          };
        };

        return {
          key: mark.key,
          mark,
          orbit:
            xSignal && ySignal
              ? buildOrbitSeries(
                  xSignal,
                  ySignal,
                  mark.orbitPair.x.pickupAngle,
                  mark.orbitPair.y.pickupAngle,
                  unitLabel(ySignal.units, String(ySignal.units)),
                )
              : null,
          xSignal: signalChart(xSignal),
          ySignal: signalChart(ySignal),
        };
      });
    },
    { success: false },
  );
  const loadChartsRef = useRef(loadCharts);
  const marksRef = useRef(marks);
  useEffect(() => {
    loadChartsRef.current = loadCharts;
  }, [loadCharts]);
  useEffect(() => {
    marksRef.current = marks;
  }, [marks]);

  useEffect(() => {
    if (!requestKey) {
      setLoadError(false);
      return;
    }
    setLoadError(false);
    let cancelled = false;
    void loadChartsRef
      .current(undefined, marksRef.current)
      .then(panels => {
        if (!cancelled && panels) {
          setLoadError(false);
          setLoaded({ key: requestKey, panels });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          setLoaded({ key: requestKey, panels: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestKey]);

  const hasLoadError = dataMarksError || loadError;
  const panels = !requestKey || hasLoadError ? [] : loaded?.panels ?? [];
  const isLoading = requestKey !== "" && !hasLoadError && loaded?.key !== requestKey;
  const [frontKey, setFrontKey] = useState<string | null>(null);
  const orbitColorByKeyRef = useRef<Map<string, string>>(new Map());
  const activeKey = panels.some(panel => panel.key === frontKey) ? frontKey : panels[panels.length - 1]?.key ?? null;
  const overlayPanels = useMemo(() => {
    if (!toolbarState.orbitOverlay) {
      return panels;
    }

    return panels.map(panel => {
      const knownColor = orbitColorByKeyRef.current.get(panel.key);
      const color = knownColor ?? lineColors[orbitColorByKeyRef.current.size % lineColors.length];
      if (!knownColor) {
        orbitColorByKeyRef.current.set(panel.key, color);
      }

      return {
        ...panel,
        orbit: panel.orbit ? { ...panel.orbit, color, seriesKey: panel.key } : null,
        xSignal: panel.xSignal ? { ...panel.xSignal, color } : null,
        ySignal: panel.ySignal ? { ...panel.ySignal, color } : null,
      };
    });
  }, [panels, toolbarState.orbitOverlay]);

  useEffect(() => {
    if (!hasLoadError) return;
    enqueueSnackbar(formatMessage({ id: "pages.speed.orbit.loadError" }), { variant: "error" });
  }, [enqueueSnackbar, formatMessage, hasLoadError]);
  const externalControls = useMemo(
    () => ({
      hideInlineControls: true,
      rotationDirection:
        toolbarState.rotationDirection === ROTATION_DIRECTION.UNDEFINED
          ? undefined
          : mapToolbarRotationDirection(toolbarState.rotationDirection),
      showOrbitPoints: toolbarState.showOrbitPoints,
      orbitFilterOpen: toolbarState.orbitFilterOpen,
      appliedOrbitBins: toolbarState.orbitAppliedBins,
      appliedBandpassHz: toolbarState.orbitAppliedBandpassHz,
      showOriginalForm: toolbarState.orbitShowOriginalForm,
      onOrbitFilterOpenChange: (open: boolean) => setToolbarState({ orbitFilterOpen: open }),
      onAppliedOrbitBinsChange: (bins: readonly number[]) => setToolbarState({ orbitAppliedBins: [...bins] }),
      onAppliedBandpassHzChange: (value: { lo: number; hi: number } | null) =>
        setToolbarState({ orbitAppliedBandpassHz: value }),
      onShowOriginalFormChange: (enabled: boolean) => setToolbarState({ orbitShowOriginalForm: enabled }),
    }),
    [setToolbarState, toolbarState],
  );
  const bandEnabled = toolbarState.orbitBandConstruction && !toolbarState.orbitRevolutionConstruction;
  const revolutionEnabled = toolbarState.orbitRevolutionConstruction && !toolbarState.orbitBandConstruction;
  const bandSelectionRef = useRef(toolbarState.orbitBandSelection);
  const revolutionSelectionRef = useRef(toolbarState.orbitRevolutionSelection);
  const panelsRef = useRef(panels);
  bandSelectionRef.current = toolbarState.orbitBandSelection;
  revolutionSelectionRef.current = toolbarState.orbitRevolutionSelection;
  panelsRef.current = panels;
  const placeBandPoint = useCallback(
    (panelKey: string, nativeX: number, datasetIndex: number) => {
      const next = placeOrbitBandPoint(bandSelectionRef.current, panelKey, nativeX, datasetIndex);
      bandSelectionRef.current = next;
      setToolbarState({ orbitBandSelection: next });
    },
    [setToolbarState],
  );
  const clearBandSelection = useCallback(() => {
    bandSelectionRef.current = null;
    setToolbarState({ orbitBandSelection: null });
  }, [setToolbarState]);
  const band = useMemo<IOrbitBandControls>(
    () => ({
      enabled: bandEnabled,
      selection: bandEnabled ? toolbarState.orbitBandSelection : null,
      onPoint: placeBandPoint,
      onClear: clearBandSelection,
    }),
    [bandEnabled, clearBandSelection, placeBandPoint, toolbarState.orbitBandSelection],
  );
  const placeRevolutionPoint = useCallback(
    (panelKey: string, nativeX: number, datasetIndex: number) => {
      const panel = panelsRef.current.find(item => item.key === panelKey);
      const next = placeOrbitRevolutionCursor(
        panelKey,
        nativeX,
        datasetIndex,
        panel?.xSignal ?? panel?.ySignal ?? null,
      );
      if (!next) {
        return;
      }

      const previous = revolutionSelectionRef.current;
      if (
        previous &&
        previous.panelKey === next.panelKey &&
        previous.cursorNativeX === next.cursorNativeX &&
        sameRevolutionBand(previous, next) &&
        previous.datasetIndex === next.datasetIndex
      ) {
        return;
      }

      revolutionSelectionRef.current = next;
      setToolbarState({ orbitRevolutionSelection: next });
    },
    [setToolbarState],
  );
  const stepRevolution = useCallback(
    (direction: -1 | 1) => {
      const current = revolutionSelectionRef.current;
      if (!current) {
        return false;
      }

      const panel = panelsRef.current.find(item => item.key === current.panelKey);
      const next = shiftOrbitRevolutionCursor(current, direction, panel?.xSignal ?? panel?.ySignal ?? null);
      if (!next) {
        return false;
      }

      revolutionSelectionRef.current = next;
      setToolbarState({ orbitRevolutionSelection: next });
      return true;
    },
    [setToolbarState],
  );
  const revolution = useMemo<IOrbitRevolutionControls>(
    () => ({
      enabled: revolutionEnabled,
      selection: revolutionEnabled ? toolbarState.orbitRevolutionSelection : null,
      onPoint: placeRevolutionPoint,
      onStep: stepRevolution,
    }),
    [placeRevolutionPoint, revolutionEnabled, stepRevolution, toolbarState.orbitRevolutionSelection],
  );
  const markerPointsWarningKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const revolutionOn = toolbarState.orbitRevolutionConstruction;
    const pointsOn = toolbarState.showOrbitPoints;
    if (!pointsOn && !revolutionOn) {
      markerPointsWarningKeyRef.current = null;
      return;
    }

    if (isLoading) {
      return;
    }

    const signals = panelSignals(panels);
    if (!signals.length || canShowMarkerPoints(signals)) {
      return;
    }

    const warningKey = signals
      .map(signal => `${signal.idMeasure ?? ""}:${signal.rangeType ?? ""}:${signal.items.length}`)
      .join("|");
    if (markerPointsWarningKeyRef.current !== warningKey) {
      markerPointsWarningKeyRef.current = warningKey;
      enqueueSnackbar(formatMessage({ id: "pages.scheme.analytics.orbitMarkerPointsSyncOnly" }), {
        variant: "warning",
      });
    }

    setToolbarState({
      showOrbitPoints: false,
      orbitRevolutionConstruction: false,
      orbitRevolutionSelection: null,
    });
  }, [
    enqueueSnackbar,
    formatMessage,
    isLoading,
    panels,
    setToolbarState,
    toolbarState.orbitRevolutionConstruction,
    toolbarState.showOrbitPoints,
  ]);

  useEffect(() => {
    if (!toolbarState.orbitRevolutionConstruction || toolbarState.showOrbitPoints || isLoading) {
      return;
    }

    const signals = panelSignals(panels);
    if (!signals.length || !canShowMarkerPoints(signals)) {
      return;
    }

    setToolbarState({ showOrbitPoints: true });
  }, [isLoading, panels, setToolbarState, toolbarState.orbitRevolutionConstruction, toolbarState.showOrbitPoints]);

  useEffect(() => {
    if (bandSelectionRef.current == null && revolutionSelectionRef.current == null) {
      return;
    }

    bandSelectionRef.current = null;
    revolutionSelectionRef.current = null;
    setToolbarState({ orbitBandSelection: null, orbitRevolutionSelection: null });
  }, [requestKey, setToolbarState]);

  if (hasLoadError || !marks.length) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Typography color="textSecondary" align="center" sx={{ fontSize: 13 }}>
          {formatMessage({ id: hasLoadError ? "pages.speed.orbit.loadError" : "pages.speed.orbit.empty" })}
        </Typography>
      </Box>
    );
  }

  const fillSingleOrbit = !toolbarState.orbitOverlay && panels.length === 1;

  return (
    <ProgressOverlay isLoading={isLoading} wrapperSx={{ height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          overflow: toolbarState.orbitOverlay || fillSingleOrbit ? "hidden" : "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: toolbarState.orbitOverlay ? 0 : 1,
        }}
      >
        {toolbarState.orbitOverlay ? (
          <OrbitOverlay
            panels={overlayPanels}
            activeKey={activeKey}
            onActivate={setFrontKey}
            externalControls={externalControls}
            signalTimeScale={toolbarState.signalTimeScale}
            trainRotationDirection={trainRotationDirection}
            dataFormat={profile.interfaceSettings.dataFormat}
            band={band}
            revolution={revolution}
          />
        ) : (
          panels.map((panel, index) => (
            <OrbitPanel
              key={panel.key}
              panel={panel}
              fill={fillSingleOrbit}
              externalControls={externalControls}
              hostFilterDialog={index === 0}
              signalTimeScale={toolbarState.signalTimeScale}
              trainRotationDirection={trainRotationDirection}
              dataFormat={profile.interfaceSettings.dataFormat}
              band={band}
              revolution={revolution}
            />
          ))
        )}
      </Box>
    </ProgressOverlay>
  );
};
